import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import StarRatingComponent from 'react-star-rating-component';
import ImageHeader from '../../elements/ImageHeader';
import Text from '../../elements/Text';
import Box from '../../elements/Box';
import Icon from '../../elements/Icon';
import FareItemOriginDestination from '../_commons/atoms/FareItemOriginDestination';
import Separator from '../_commons/atoms/Separator';
import FareItemPrice from '../_commons/atoms/FareItemPrice';
import { usePlural } from '@front10/utils';
import { handleFareItemInteraction } from '../_commons/commonFns';

const Price = (props) => {
  const {
    fareItemPricePerNight,
    fareItemPriceTotal,
    showPricePerNight,
    showPriceTotal,
    fareItemPricePerNightContainer,
    fareItemPriceTotalContainer,
    fareItemPricePerNightAndTotalContainer,
    separatorStyles,
    data: { formattedPriceRate: priceRate, formattedTotalPrice: totalPrice, rateType }
  } = props;
  const { translate } = useContext(Context);
  return (
    <Box {...fareItemPricePerNightAndTotalContainer}>
      {showPricePerNight && priceRate && (
        <Box {...fareItemPricePerNightContainer}>
          <FareItemPrice {...fareItemPricePerNight} formattedTotalPrice={priceRate} />
          <Separator {...separatorStyles} />
          <Text textTransform="lowercase">{translate(`cmp-price-${rateType}-text`, 'Night')}</Text>
        </Box>
      )}
      {showPriceTotal && totalPrice && (
        <Box {...fareItemPriceTotalContainer}>
          <FareItemPrice {...fareItemPriceTotal} formattedTotalPrice={totalPrice} />
          <Text textTransform="lowercase" marginLeft="1">
            {translate('cmp-price-total-text', 'total')}
          </Text>
        </Box>
      )}
    </Box>
  );
};

Price.propTypes = {
  fareItemPricePerNight: PropTypes.objectOf(PropTypes.any),
  fareItemPricePerNightContainer: PropTypes.objectOf(PropTypes.any),
  fareItemPriceTotal: PropTypes.objectOf(PropTypes.any),
  fareItemPriceTotalContainer: PropTypes.objectOf(PropTypes.any),
  fareItemPricePerNightAndTotalContainer: PropTypes.objectOf(PropTypes.any),
  separatorStyles: PropTypes.objectOf(PropTypes.any),
  showPricePerNight: PropTypes.bool,
  showPriceTotal: PropTypes.bool,
  data: PropTypes.objectOf(PropTypes.any)
};

Price.defaultProps = {
  fareItemPricePerNight: {},
  fareItemPriceTotal: {},
  fareItemPricePerNightContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  fareItemPriceTotalContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '1'
  },
  fareItemPricePerNightAndTotalContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
    marginTop: 'auto',
    style: { lineHeight: '1' }
  },
  separatorStyles: {
    content: '/',
    paddingLeft: '0',
    paddingRight: '0'
  },
  showPricePerNight: true,
  showPriceTotal: true,
  data: {}
};

const Stars = (props) => {
  const {
    reviewItemStyle,
    reviewQtyStyle,
    hostCategoryStyle,
    starRatingStyle,
    data: { popularity, travelClass, reviewsQty }
  } = props;
  const hostCategory = travelClass; // TODO
  return (
    <Box display="flex" flexDirection="row" {...reviewItemStyle}>
      <StarRatingComponent
        editing={false}
        name="rate1"
        starCount={5}
        value={parseFloat(popularity)}
        renderStarIcon={(index, value) => {
          return (
            <Text {...starRatingStyle}>
              <Icon
                icon={index <= value ? 'star-full' : 'star-empty'}
                iconColor="white"
                size={14}
              />
            </Text>
          );
        }}
        renderStarIconHalf={() => {
          return (
            <Text {...starRatingStyle}>
              <Icon icon="star-half" iconColor="white" size={14} />
            </Text>
          );
        }}
      />
      <Text {...reviewQtyStyle}>{` ${reviewsQty} `}</Text>
      {hostCategory && (
        <>
          <Text>·</Text>
          <Text {...hostCategoryStyle}>{` ${hostCategory} `}</Text>
        </>
      )}
    </Box>
  );
};

Stars.propTypes = {
  reviewItemStyle: PropTypes.objectOf(PropTypes.any),
  reviewQtyStyle: PropTypes.objectOf(PropTypes.any),
  starRatingStyle: PropTypes.objectOf(PropTypes.any),
  hostCategoryStyle: PropTypes.objectOf(PropTypes.any),
  data: PropTypes.objectOf(PropTypes.any)
};

Stars.defaultProps = {
  reviewItemStyle: {},
  reviewQtyStyle: {},
  starRatingStyle: {},
  hostCategoryStyle: {},
  data: {}
};

const Capacity = (props) => {
  const {
    fareItemCapacity,
    data: { beds }
  } = props;
  const translate = usePlural();
  return (
    <Box display="flex" {...fareItemCapacity}>
      <Text floatLeft>{translate({ key: 'cmp-airbnb-item-beds', count: beds })}</Text>
    </Box>
  );
};

Capacity.propTypes = {
  fareItemCapacity: PropTypes.objectOf(PropTypes.any),
  data: PropTypes.objectOf(PropTypes.any)
};

Capacity.defaultProps = {
  fareItemCapacity: {},
  data: {}
};

const ItemCard = (props) => {
  const {
    fareItemCard,
    fareItemOriginDestination,
    originDestinationCapacity,
    titlePlaceSettings,
    data,
    data: { placeDescription: titlePlace, originCity: roomType }
  } = props;
  return (
    <Box display="flex" flexDirection="column" {...fareItemCard}>
      <Box display="flex" flexDirection="column" height="100">
        <Box {...originDestinationCapacity}>
          <FareItemOriginDestination
            {...fareItemOriginDestination}
            {...data}
            originCity={roomType}
          />
          <Separator paddingLeft="0" content="·" />
          <Capacity {...props} />
        </Box>
        <Text {...titlePlaceSettings} rtlReverse>
          {titlePlace}
        </Text>
        <Stars {...props} />
        <Price {...props} />
      </Box>
    </Box>
  );
};

ItemCard.propTypes = {
  fareItemCard: PropTypes.objectOf(PropTypes.any),
  fareItemOriginDestination: PropTypes.objectOf(PropTypes.any),
  originDestinationCapacity: PropTypes.objectOf(PropTypes.any),
  titlePlaceSettings: PropTypes.objectOf(PropTypes.any),
  data: PropTypes.objectOf(PropTypes.any)
};

ItemCard.defaultProps = {
  fareItemCard: {},
  fareItemOriginDestination: {},
  originDestinationCapacity: {},
  titlePlaceSettings: {},
  data: {}
};

const OfferVerticalCard = (props) => {
  const {
    imageProps,
    data,
    showImage,
    focusReferences,
    tabIndex,
    airbnbItemStyles,
    showPill,
    fareItemPill,
    fareItemPillContainer,
    navigationInfo
  } = props;

  const { handleClick, handleItemKeyDown } = handleFareItemInteraction(props);

  const { direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  return (
    <Box
      focusable={tabIndex}
      tabIndex={tabIndex ? 0 : -1}
      focusReferences={focusReferences}
      onKeyDown={handleItemKeyDown}
      onClick={handleClick}
      className={className}
      style={style}
      dataTestId="offerVerticalLayoutRow"
    >
      {showImage ? (
        <ImageHeader {...imageProps} image={data.destinationImage}>
          <>
            {showPill && fareItemPill && (
              <Box {...fareItemPillContainer}>
                <Text {...fareItemPill}>{data ? data.navigationInfo : navigationInfo}</Text>
              </Box>
            )}
            <ItemCard {...airbnbItemStyles} {...props} data={data} />
          </>
        </ImageHeader>
      ) : (
        <>
          <ItemCard {...airbnbItemStyles} {...props} data={data} />
        </>
      )}
    </Box>
  );
};

OfferVerticalCard.propTypes = {
  /**
   * Class name of header
   */
  className: PropTypes.string,
  /**
   * Css style applied to the component
   */
  style: PropTypes.objectOf(PropTypes.any),
  /**
   * Image of destination.
   */
  image: PropTypes.string,
  /**
   * image props.
   */
  imageProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Name of destination.
   */
  name: PropTypes.string,
  /**
   * iataCode of destination.
   */
  iataCode: PropTypes.string,
  /**
   * data to render by the item
   */
  data: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  /**
   * Header props
   */
  header: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  interationHotZone: PropTypes.oneOf(['smart', 'whole', 'button']),
  /**
   * Determine if the image will be displayed in the component.
   */
  showImage: PropTypes.bool,
  /**
   * Show or hide the city name
   */
  showCityName: PropTypes.bool,
  /**
   * OnKeyDown function.
   */
  onKeyDown: PropTypes.func,
  /**
   * FocusReferences functions
   */
  focusReferences: PropTypes.func,
  /**
   * Turn off or on tabIndex in the OfferVerticalCard
   */
  tabIndex: PropTypes.bool,
  /**
   * Settings of seeMore component
   */
  seeMore: PropTypes.objectOf(PropTypes.any),
  /**
   * Show or hide see more
   */
  showSeeMore: PropTypes.bool,
  /**
   * Site edition of the component
   */
  siteEdition: PropTypes.string,
  /**
   * Url base for component see more
   */
  seeMoreBasicUrl: PropTypes.string,
  /**
   * Traffix variants
   */
  traffixVariant: PropTypes.string,
  /**
   * OnClick event
   */
  onClick: PropTypes.func,
  /**
   * Call to action onClick event
   */
  callToActionOnClick: PropTypes.func,
  /**
   * Settings for the card
   */
  airbnbItemStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Pill
   */
  showPill: PropTypes.bool,
  /**
   * navigationText alernative
   */
  navigationInfo: PropTypes.string,
  /**
   * Props of fareItemPill
   */
  fareItemPill: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemPillContainer
   */
  fareItemPillContainer: PropTypes.objectOf(PropTypes.any)
};
OfferVerticalCard.defaultProps = {
  className: '',
  style: null,
  image: '',
  name: '',
  iataCode: '',
  data: null,
  imageProps: {},
  header: {},
  interationHotZone: 'smart',
  onClick: (props) => props,
  callToActionOnClick: (props) => props,
  showImage: true,
  showCityName: true,
  onKeyDown: () => {},
  focusReferences: () => {},
  tabIndex: false,
  seeMore: {
    display: 'block',
    textAlign: 'center',
    textColor: 'dark',
    fontWeight: 'bold',
    padding: '2'
  },
  showSeeMore: true,
  siteEdition: null,
  seeMoreBasicUrl: null,
  traffixVariant: 'ci',
  airbnbItemStyles: {
    showCapacity: true,
    fareItemCapacity: {
      fontSize: '12px'
    }
  },
  showPill: true,
  navigationInfo: '',
  fareItemPill: {
    bgColor: 'dark',
    textColor: 'white',
    marginTop: '2',
    marginRight: '2',
    paddingTop: '2px',
    paddingBottom: '2px',
    textAlign: 'center',
    rounded: '3',
    fontSize: '3',
    fontWeight: 'bold',
    style: { minWidth: '40px' }
  },
  fareItemPillContainer: {
    display: 'flex',
    justifyContent: 'end'
  }
};
OfferVerticalCard.componentName = 'OfferVerticalCard';

export default OfferVerticalCard;
